import { isIOSNewVersion } from "./util";

/**
 * iOS 26 "Liquid Glass" fixes.
 *
 * The floating bottom nav (~83px) introduced by iOS 26 overlays app content
 * and causes three problems that this module addresses at the DOM level:
 *  - Issue #1: Body scroll rubber-bands over content hidden behind the nav or
 *              bottom sticky page sections.
 *  - Issue #2: Page-level bottom-anchored buttons/banners (position:fixed or
 *              position:absolute) overlap the nav.
 *  - Issue #3: VDS Modal button rows overlap the nav.
 *
 * All adjustments are gated by common.isIOSNewVersion() and are wired into
 * library.init so every consuming app benefits automatically after calling
 * init(). Init is idempotent — repeat calls (e.g., on route change) are safe.
 */

const FLOATING_NAV_OFFSET_PX = 85;
const MODAL_BUTTON_MARGIN_PX = 100;
const BOTTOM_ANCHOR_TOLERANCE_PX = 30;

const VDS_MODAL_CLASS_TOKEN = "VDS__MON__modal-module";
const VDS_MODAL_BODY_CLASS_TOKEN = "VDS__MON__modal-body-module";
const VDS_MODAL_SELECTOR = `[class*="${VDS_MODAL_CLASS_TOKEN}"]`;

const FIXED_ADJUSTED_ATTR = "iosLgFixedBottomAdjusted";
const MODAL_BTN_ADJUSTED_ATTR = "iosLgModalButtonAdjusted";
const INIT_FLAG_ATTR = "iosLgInitialized";

const MODAL_ACTION_SELECTOR = "button, a";
const BOTTOM_ACTION_SELECTOR = "button, a, [role=\"button\"]";
const BOTTOM_STICKY_SECTION_TAGS = new Set(["DIV", "FOOTER"]);

const hasClassStartingWith = (element, token) => {
    const className = element && element.className;
    if (typeof className !== "string" || className.length === 0) return false;
    return className.split(/\s+/).some(cls => cls.startsWith(token));
};

const isModalCloseAction = (element) =>
    element.dataset.testid === "modal-close-button";

/**
 * Returns the modal's trailing bottom action (button or link), excluding the
 * header close icon. When content scrolls, falls back to the lowest action in
 * the modal's content flow so footer CTAs still receive the nav offset.
 */
const findLastBottomModalAction = (modal) => {
    const candidates = Array.from(modal.querySelectorAll(MODAL_ACTION_SELECTOR))
        .filter(el => el instanceof HTMLElement)
        .filter(el => !isModalCloseAction(el));

    if (candidates.length === 0) return null;

    const modalRect = modal.getBoundingClientRect();
    const bottomAnchored = candidates.filter(el => {
        const rect = el.getBoundingClientRect();
        return modalRect.bottom - rect.bottom <= BOTTOM_ANCHOR_TOLERANCE_PX;
    });

    if (bottomAnchored.length > 0) {
        return bottomAnchored[bottomAnchored.length - 1];
    }

    if (modal.scrollHeight > modal.clientHeight) {
        return candidates.reduce((lowest, el) =>
            el.getBoundingClientRect().bottom > lowest.getBoundingClientRect().bottom ? el : lowest,
        );
    }

    return null;
};

const isVDSModalNode = (element) => {
    if (!element || !(element instanceof HTMLElement)) return false;
    return hasClassStartingWith(element, VDS_MODAL_CLASS_TOKEN);
};

/**
 * Issue #2: Push "sticky" bottom-anchored elements (buttons/banners) above
 * the floating bottom nav by setting their bottom offset to 85px. Covers both
 * position:fixed elements (anchored to the viewport) and position:absolute
 * elements whose containing block extends to the viewport bottom.
 */
const adjustBottomAnchoredElement = (element) => {
    if (!(element instanceof HTMLElement)) return;
    if (!BOTTOM_STICKY_SECTION_TAGS.has(element.tagName)) return;
    if (element.dataset[FIXED_ADJUSTED_ATTR] === "true") return;

    const computed = window.getComputedStyle(element);
    if (computed.position !== "fixed" && computed.position !== "absolute") return;

    // VDS Modals are also position:fixed but are handled by Issue #3.
    if (element.closest(VDS_MODAL_SELECTOR)) return;

    const isStickyFooter = element.tagName === "FOOTER";
    if (!isStickyFooter && !element.querySelector(BOTTOM_ACTION_SELECTOR)) return;

    // Only touch elements anchored to the bottom of the viewport (sticky
    // buttons / banners) — never full-height overlays or absolutely
    // positioned elements that live somewhere in the middle of the page.
    const rect = element.getBoundingClientRect();
    const bottomOffset = parseFloat(computed.bottom);
    const isCssBottomAnchored =
        Number.isFinite(bottomOffset) && bottomOffset <= BOTTOM_ANCHOR_TOLERANCE_PX;
    const distanceFromViewportBottom = window.innerHeight - rect.bottom;
    const isRectBottomAnchored =
        distanceFromViewportBottom <= BOTTOM_ANCHOR_TOLERANCE_PX;
    if (!isCssBottomAnchored && !isRectBottomAnchored) return;

    element.dataset[FIXED_ADJUSTED_ATTR] = "true";
    element.style.bottom = `${FLOATING_NAV_OFFSET_PX}px`;
};

const scanBottomAnchoredElements = (root) => {
    root.querySelectorAll("div, footer").forEach(adjustBottomAnchoredElement);
};

const scanAncestorBottomAnchoredElements = (node) => {
    if (!(node instanceof HTMLElement)) return;

    let ancestor = node.parentElement;
    while (ancestor) {
        if (BOTTOM_STICKY_SECTION_TAGS.has(ancestor.tagName)) {
            adjustBottomAnchoredElement(ancestor);
        }
        ancestor = ancestor.parentElement;
    }
};

/**
 * Issue #3 margin target: prefer the scrollable modal body so header chrome
 * (close icon, top spacing) is unaffected. Falls back to the last non-sticky
 * div inside the modal — never the modal root itself.
 */
const isNonStickyDiv = (element) => {
    const { position } = window.getComputedStyle(element);
    return position !== "sticky" && position !== "fixed" && position !== "absolute";
};

const findModalMarginTarget = (modal) => {
    const modalBody = modal.querySelector(`div[class*="${VDS_MODAL_BODY_CLASS_TOKEN}"]`);
    if (modalBody instanceof HTMLElement) return modalBody;

    const divs = Array.from(modal.querySelectorAll("div")).filter(
        el =>
            el instanceof HTMLElement
            && el !== modal
            && !hasClassStartingWith(el, VDS_MODAL_CLASS_TOKEN)
            && isNonStickyDiv(el),
    );

    return divs.length > 0 ? divs[divs.length - 1] : null;
};

/**
 * Issue #3: When a VDS Modal opens, add a bottom margin so its trailing
 * button (or the button-row wrapping it) clears the floating nav. Only
 * applies when the modal actually overlaps the nav's danger zone or its
 * content overflows the viewport — a short, centered modal that never
 * touches the nav is left untouched.
 */
const adjustVDSModalNode = (modal) => {
    if (!(modal instanceof HTMLElement)) return;

    const marginTarget = findModalMarginTarget(modal);
    if (!marginTarget) return;
    if (marginTarget.dataset[MODAL_BTN_ADJUSTED_ATTR] === "true") return;

    const lastAction = findLastBottomModalAction(modal);
    if (!lastAction) return;

    // Defer measurement to the next frame so the modal's enter animation /
    // layout has settled before we sample element positions.
    window.requestAnimationFrame(() => {
        if (!lastAction.isConnected) return;
        if (!marginTarget.isConnected) return;
        if (marginTarget.dataset[MODAL_BTN_ADJUSTED_ATTR] === "true") return;

        const rect = lastAction.getBoundingClientRect();
        const overlapsFloatingNav =
            rect.bottom > window.innerHeight - FLOATING_NAV_OFFSET_PX;
        const modalContentOverflows = modal.scrollHeight > modal.clientHeight;

        if (!overlapsFloatingNav && !modalContentOverflows) return;

        marginTarget.dataset[MODAL_BTN_ADJUSTED_ATTR] = "true";
        marginTarget.style.marginBottom = `${MODAL_BUTTON_MARGIN_PX}px`;
    });
};

const scanVDSModals = (root) => {
    root.querySelectorAll(VDS_MODAL_SELECTOR).forEach(adjustVDSModalNode);
};

const parseCssPx = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const isBottomStickySection = (element) => {
    if (!BOTTOM_STICKY_SECTION_TAGS.has(element.tagName)) return false;
    if (element.closest(VDS_MODAL_SELECTOR)) return false;

    const computed = window.getComputedStyle(element);
    const isBottomAnchored = computed.position === "sticky" || computed.position === "fixed";
    if (!isBottomAnchored) return false;

    // Issue #2 shifts fixed footers to bottom: 85px — still count them for padding.
    if (element.dataset[FIXED_ADJUSTED_ATTR] === "true") return true;

    const bottom = parseCssPx(computed.bottom);
    if (bottom === null || bottom > BOTTOM_ANCHOR_TOLERANCE_PX) return false;

    const top = parseCssPx(computed.top);
    if (top !== null && top <= BOTTOM_ANCHOR_TOLERANCE_PX) return false;

    return true;
};

const getStickyBottomSectionHeightPx = () => {
    let maxHeight = 0;

    document.body.querySelectorAll("div, footer").forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        if (!isBottomStickySection(element)) return;

        const height = Math.max(element.getBoundingClientRect().height, element.offsetHeight);
        if (height > maxHeight) maxHeight = height;
    });

    return Math.ceil(maxHeight);
};

const buildBodyPaddingBottom = (stickyHeightPx) => {
    if (stickyHeightPx <= 0) return "env(safe-area-inset-bottom)";
    return `calc(env(safe-area-inset-bottom) + ${stickyHeightPx}px)`;
};

const applyBodyPadding = () => {
    // Issue #1: prevent the page from bouncing back over content that sits
    // behind the floating bottom nav / home indicator, plus any bottom-sticky
    // page sections (div or footer) that reduce the reachable scroll range.
    document.body.style.paddingBottom = buildBodyPaddingBottom(getStickyBottomSectionHeightPx());
};

const processAddedNode = (node) => {
    if (!(node instanceof HTMLElement)) return;

    if (isVDSModalNode(node)) adjustVDSModalNode(node);
    if (BOTTOM_STICKY_SECTION_TAGS.has(node.tagName)) adjustBottomAnchoredElement(node);

    // Child content of an already-open modal may mount later (async data,
    // form reveals, etc.). Re-check the enclosing modal in that case.
    const enclosingModal = node.closest && node.closest(VDS_MODAL_SELECTOR);
    if (enclosingModal) adjustVDSModalNode(enclosingModal);

    scanBottomAnchoredElements(node);
    scanAncestorBottomAnchoredElements(node);
    scanVDSModals(node);
    applyBodyPadding();
};

let mutationObserver = null;

const VIEWPORT_META_DEFAULT_CONTENT =
    "width=device-width, initial-scale=1.0, viewport-fit=cover";
const VIEWPORT_FIT_COVER = "viewport-fit=cover";

/**
 * `env(safe-area-inset-bottom)` only resolves to a non-zero value when the
 * document opts into the safe-area insets via `viewport-fit=cover` on its
 * viewport meta tag. Add / patch the tag so the Issue #1 padding actually
 * takes effect on iOS 26 devices.
 */
const ensureViewportMetaTag = () => {
    const existingMeta = document.querySelector('meta[name="viewport"]');

    if (!existingMeta) {
        const meta = document.createElement("meta");
        meta.name = "viewport";
        meta.content = VIEWPORT_META_DEFAULT_CONTENT;
        document.head.appendChild(meta);
        return;
    }

    const currentContent = (existingMeta.getAttribute("content") || "").trim();
    if (currentContent.indexOf(VIEWPORT_FIT_COVER) !== -1) return;

    let nextContent;
    if (currentContent.length === 0) {
        nextContent = VIEWPORT_FIT_COVER;
    } else if (currentContent.endsWith(",")) {
        nextContent = `${currentContent} ${VIEWPORT_FIT_COVER}`;
    } else {
        nextContent = `${currentContent}, ${VIEWPORT_FIT_COVER}`;
    }

    existingMeta.setAttribute("content", nextContent);
};

const iosLiquidGlass = {
    /**
     * Called from library.init. Safe to call repeatedly — the observer and
     * body-padding tweak are applied only once per document.
     */
    init() {
        try {
            if (typeof window === "undefined" || typeof document === "undefined") return;
            if (!isIOSNewVersion()) return;
            if (document.body.dataset[INIT_FLAG_ATTR] === "true") return;

            document.body.dataset[INIT_FLAG_ATTR] = "true";

            ensureViewportMetaTag();
            scanBottomAnchoredElements(document.body);
            scanVDSModals(document.body);
            applyBodyPadding();

            mutationObserver = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    mutation.addedNodes.forEach(processAddedNode);
                }
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        } catch (ex) {
            console.error("iosLiquidGlass init ERROR---", ex);
        }
    }

};

export default iosLiquidGlass;

